import { useId, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "../Button";
import type { IconName } from "../Icon";
import { ErrorBanner } from "../feedback/Feedback";
import { Field } from "../form/Field";
import { Input } from "../form/controls";
import { Modal, type ModalSize } from "./Modal";

/** Case, accents' encoding and stray spaces don't count when retyping a name. */
const normalize = (s: string) => s.normalize("NFC").trim().toLowerCase();

export interface ConfirmDialogProps {
  title: string;
  /** The consequence, in plain words: what changes and for whom. */
  children: ReactNode;
  confirmLabel: string;
  /** `danger` for destructive or privilege-granting actions, `primary` for the rest. */
  tone?: "danger" | "primary";
  icon?: IconName;
  /** The most dangerous actions: the confirm button stays disabled until this exact text is typed. */
  typeToConfirm?: string;
  cancelLabel?: string;
  pending?: boolean;
  error?: unknown;
  size?: ModalSize;
  onConfirm: () => void;
  onClose: () => void;
}

/** Asks before an action that is hard to undo, stating its consequence. */
export function ConfirmDialog({
  title, children, confirmLabel, tone = "danger", icon, typeToConfirm, cancelLabel = "Huỷ", pending, error, size = "sm", onConfirm, onClose,
}: ConfirmDialogProps) {
  const formId = useId();
  const [typed, setTyped] = useState("");
  const ready = typeToConfirm === undefined || normalize(typed) === normalize(typeToConfirm);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready && !pending) onConfirm();
  };
  return (
    <Modal title={title} size={size} onClose={() => !pending && onClose()} footer={<>
      {/* Danger without a phrase to type: start on the safe button, so Enter does nothing harmful. */}
      <Button variant="ghost" disabled={pending} autoFocus={tone === "danger" && typeToConfirm === undefined} onClick={onClose}>{cancelLabel}</Button>
      <Button type="submit" form={formId} variant={tone} icon={icon} loading={pending} disabled={!ready}>{confirmLabel}</Button>
    </>}>
      <form id={formId} className="stack gap-md" onSubmit={submit} noValidate>
        <ErrorBanner error={error} />
        <div className="stack gap-sm">{children}</div>
        {typeToConfirm !== undefined && (
          <Field label={<>Gõ <strong className="confirm-phrase">{typeToConfirm}</strong> để xác nhận</>}>
            <Input autoComplete="off" spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} />
          </Field>
        )}
      </form>
    </Modal>
  );
}

/**
 * A trigger plus its ConfirmDialog. `onConfirm` may return a promise (e.g.
 * `mutation.mutateAsync`): the dialog shows progress, closes when it
 * resolves and keeps the error on screen when it rejects.
 */
export function ConfirmAction({ trigger, onConfirm, ...dialog }: Omit<ConfirmDialogProps, "onConfirm" | "onClose" | "pending" | "error"> & {
  trigger: (open: () => void) => ReactNode;
  onConfirm: () => unknown;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      setOpen(false);
    } catch (e) {
      setError(e);
    } finally {
      setPending(false);
    }
  };
  return (
    <>
      {trigger(() => { setError(null); setOpen(true); })}
      {open && <ConfirmDialog {...dialog} pending={pending} error={error} onConfirm={() => void confirm()} onClose={() => setOpen(false)} />}
    </>
  );
}
